/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import { Issue, IssueStatus, EstimateSize } from '@shared/utils/issues';
import { Save } from 'lucide-react';

interface ReleaseFormProps {
  formData: Partial<Issue>;
  statusOptions: IssueStatus[];
  estimateOptions: EstimateSize[];
  onInputChange: (field: keyof Issue, value: any) => void;
  onArrayInputChange: (field: 'links' | 'labels', value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isEditing: boolean;
}

export function ReleaseForm({
  formData,
  statusOptions,
  estimateOptions,
  onInputChange,
  onArrayInputChange,
  onSubmit,
  onCancel,
  isEditing
}: ReleaseFormProps) {
  return (
    <div style={{ 
      border: '1px solid #ddd', 
      borderRadius: '8px', 
      padding: '24px', 
      marginBottom: '24px',
      background: '#f9f9f9'
    }}>
      <h3 style={{ marginBottom: '20px' }}>
        {isEditing ? 'Edit Release' : 'Create New Release'}
      </h3>
      <form onSubmit={onSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div className="form-group">
            <label>Version *</label>
            <input
              type="text"
              value={formData.version || ''}
              onChange={(e) => onInputChange('version', e.target.value)}
              placeholder="e.g., 1.0.0"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => onInputChange('status', e.target.value)}
            >
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => onInputChange('title', e.target.value)}
            placeholder="Release name or description"
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => onInputChange('description', e.target.value)}
            placeholder="What's included in this release?"
            rows={4}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div className="form-group">
            <label>Assigned To</label>
            <input
              type="text"
              value={formData.assignedTo || ''}
              onChange={(e) => onInputChange('assignedTo', e.target.value)}
              placeholder="Release manager email"
            />
          </div>
          
          <div className="form-group">
            <label>Estimate</label>
            <select
              value={formData.estimate || 'M'}
              onChange={(e) => onInputChange('estimate', e.target.value)}
            >
              {estimateOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Labels</label>
          <input
            type="text"
            value={formData.labels?.join(', ') || ''}
            onChange={(e) => onArrayInputChange('labels', e.target.value)}
            placeholder="Comma-separated labels (e.g., major, breaking-changes, security)"
          />
        </div>

        <div className="form-group">
          <label>Links</label>
          <textarea
            value={formData.links?.join(', ') || ''}
            onChange={(e) => onArrayInputChange('links', e.target.value)}
            placeholder="Related issues, PRs, or documentation (comma-separated)"
            rows={2}
          />
        </div>

        <div className="form-group">
          <label>Release Notes</label>
          <textarea
            value={formData.plan || ''}
            onChange={(e) => onInputChange('plan', e.target.value)}
            placeholder="Detailed release notes, changelog, or deployment plan"
            rows={6}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
          <button type="submit" className="btn" style={{ flex: 1 }}>
            <Save size={16} style={{ marginRight: '8px' }} />
            {isEditing ? 'Update Release' : 'Create Release'}
          </button>
          <button type="button" onClick={onCancel} className="btn" style={{ background: '#f5f5f5', color: '#333' }}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

